import React, { useState, useEffect, useRef } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { authHeaders } from '../utils/auth';

const DEFAULT_LAYOUT = [
    { i: 'stats', x: 0, y: 0, w: 12, h: 2 },
    { i: 'config', x: 0, y: 2, w: 4, h: 8 },
    { i: 'logs', x: 4, y: 2, w: 8, h: 8 }
];

interface BridgeStat {
    total_requests: number;
    successful: number;
    failed: number;
    avg_latency_ms?: number;
}

interface BridgeLog {
    timestamp: string;
    level: string;
    message: string;
}

export const BridgePanel: React.FC = () => {
    const [stats, setStats] = useState<BridgeStat | null>(null);
    const [logs, setLogs] = useState<BridgeLog[]>([]);
    const [config, setConfig] = useState<Record<string, any>>({});
    const [status, setStatus] = useState<'online' | 'offline' | 'checking'>('checking');
    const logsEndRef = useRef<HTMLDivElement>(null);
    const pollRef = useRef<NodeJS.Timeout | null>(null);

    // Draggable Layout State
    const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
        const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_bridge_layout');
        if (saved) {
            try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_bridge_layout', JSON.stringify(allLayouts));
    };

    const fetchBridgeStatus = async () => {
        try {
            const res = await fetch('/v1/mcp/omni-bridge/health', { headers: authHeaders() });
            if (res.ok) {
                setStatus('online');
            } else {
                setStatus('offline');
            }
        } catch {
            setStatus('offline');
        }
    };

    const fetchStats = async () => {
        try {
            const res = await fetch('/v1/mcp/omni-bridge/stats', { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setStats(data);
            }
        } catch { }
    };

    const fetchLogs = async () => {
        try {
            const res = await fetch('/v1/mcp/omni-bridge/logs?limit=100', { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                const rawLogs = Array.isArray(data) ? data : (data.logs || data.entries || []);
                setLogs(rawLogs.slice(-100));
                setTimeout(() => (() => { const el = logsEndRef.current; if(el && el.parentElement) { el.parentElement.scrollTop = el.parentElement.scrollHeight; } })(), 50);
            }
        } catch { }
    };

    const fetchConfig = async () => {
        try {
            const res = await fetch('/v1/mcp/omni-bridge/config', { headers: authHeaders() });
            if (res.ok) {
                const data = await res.json();
                setConfig(data);
            }
        } catch { }
    };

    useEffect(() => {
        fetchBridgeStatus();
        fetchStats();
        fetchLogs();
        fetchConfig();

        // Poll logs + stats every 4s
        pollRef.current = setInterval(() => {
            fetchBridgeStatus();
            fetchStats();
            fetchLogs();
            fetchConfig();
        }, 4000);

        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const statusColor = status === 'online' ? 'text-green-400' : status === 'offline' ? 'text-red-400' : 'text-yellow-400';
    const successRate = stats
        ? stats.total_requests > 0 ? Math.round((stats.successful / stats.total_requests) * 100) : 0
        : null;

    if (!mounted) return null;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(168, 85, 247, 0.2); border: 2px dashed rgba(168, 85, 247, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                .drag-handle { cursor: grab; }
                .drag-handle:active { cursor: grabbing; }
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); font-family: monospace; }
                .panel-wrapper:hover { border-color: rgba(168, 85, 247, 0.3); }
            `}</style>

            <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
                <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_bridge_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
                    <i className="fas fa-sync-alt mr-1"></i> Reset Layout
                </button>
            </div>

            
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
                className="layout flex-1"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={50}
                onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                {/* Panel 1: Stats */}
                <div key="stats" className="panel-wrapper">
                    <div className={`p-2 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'} flex-shrink-0`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-chart-bar text-purple-400"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Bridge Stats</h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <div className={`w-2 h-2 rounded-full ${status === 'online' ? 'bg-green-500 animate-pulse' : status === 'offline' ? 'bg-red-500' : 'bg-yellow-500 animate-pulse'}`}></div>
                                <span className={`text-[9px] font-bold uppercase ${statusColor}`}>{status}</span>
                            </div>
                            <button onClick={() => { fetchBridgeStatus(); fetchStats(); fetchLogs(); fetchConfig(); }}
                                className="text-[9px] text-slate-500 hover:text-slate-300 border border-[#2a2e39] px-2 py-1 rounded">
                                <i className="fas fa-sync-alt"></i>
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto flex items-center bg-[#1e222d]">
                        {stats ? (
                            <div className="grid grid-cols-5 gap-px w-full h-full bg-[#2a2e39]">
                                {[
                                    { label: 'Total Req', value: (stats.total_requests ?? 0).toLocaleString(), color: 'text-blue-400' },
                                    { label: 'Success', value: (stats.successful ?? 0).toLocaleString(), color: 'text-green-400' },
                                    { label: 'Failed', value: (stats.failed ?? 0).toLocaleString(), color: (stats.failed ?? 0) > 0 ? 'text-red-400' : 'text-slate-500' },
                                    { label: 'Success Rate', value: `${successRate}%`, color: (successRate ?? 0) >= 90 ? 'text-green-400' : (successRate ?? 0) >= 70 ? 'text-yellow-400' : 'text-red-400' },
                                    { label: 'Avg Latency', value: stats.avg_latency_ms !== undefined ? `${Math.round(stats.avg_latency_ms)}ms` : '-', color: 'text-cyan-400' }
                                ].map(s => (
                                    <div key={s.label} className="bg-[#131722] flex flex-col justify-center items-center h-full">
                                        <p className="text-[9px] text-slate-500 uppercase tracking-widest">{s.label}</p>
                                        <p className={`text-base font-black ${s.color} mt-0.5`}>{s.value}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="w-full text-center text-slate-600 text-[10px]">No stats available — bridge may be offline</div>
                        )}
                    </div>
                </div>

                {/* Panel 2: Config */}
                <div key="config" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-sliders-h text-purple-400"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Configuration</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 bg-[#0a0c10] custom-scrollbar">
                        {Object.keys(config).length === 0 ? (
                            <div className="text-center py-12 text-slate-600">
                                <i className="fas fa-sliders-h text-3xl mb-3 block opacity-30"></i>
                                <p className="text-sm">No config loaded — bridge may be offline</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {Object.entries(config).map(([k, v]) => (
                                    <div key={k} className="flex justify-between items-center bg-[#131722] border border-[#2a2e39] rounded px-3 py-2">
                                        <span className="text-[9px] font-bold text-slate-400 uppercase break-words w-1/2">{k}</span>
                                        <span className={`text-[10px] font-mono text-right break-words w-1/2 ${v === true ? 'text-green-400' :
                                            v === false ? 'text-red-400' :
                                                typeof v === 'number' ? 'text-cyan-400' : 'text-slate-300'
                                            }`}>
                                            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Panel 3: Logs */}
                <div key="logs" className="panel-wrapper">
                    <div className={`p-3 border-b border-[#2a2e39] bg-[#131722]/60 flex items-center justify-between ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-terminal text-purple-400"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Recent Logs</h2>
                        </div>
                        <button onClick={fetchLogs} className="text-[10px] text-slate-500 hover:text-purple-400">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 bg-[#0a0c10] space-y-0.5 custom-scrollbar font-mono text-[10px]">
                        {logs.length === 0 ? (
                            <div className="text-center py-12 text-slate-600">
                                <i className="fas fa-terminal text-3xl mb-3 block opacity-30"></i>
                                <p className="text-sm">No logs yet</p>
                            </div>
                        ) : (
                            logs.map((log, i) => (
                                <div key={i} className={`flex gap-2 p-1.5 rounded ${log.level === 'ERROR' ? 'bg-red-500/5 text-red-400' :
                                    log.level === 'WARN' ? 'bg-yellow-500/5 text-yellow-400' :
                                        log.level === 'INFO' ? 'text-slate-300' : 'text-slate-500'
                                    }`}>
                                    <span className="text-slate-600 flex-shrink-0 w-16 truncate">{log.timestamp?.slice(11, 19) || '?'}</span>
                                    <span className={`flex-shrink-0 w-10 font-bold text-[9px] ${log.level === 'ERROR' ? 'text-red-500' :
                                        log.level === 'WARN' ? 'text-yellow-500' :
                                            log.level === 'INFO' ? 'text-green-500' : 'text-slate-600'
                                        }`}>{log.level}</span>
                                    <span className="flex-1 break-all">{log.message}</span>
                                </div>
                            ))
                        )}
                        <div ref={logsEndRef}></div>
                    </div>
                </div>
            </ResponsiveGridLayout>
        </div>
    );
};

export default BridgePanel;
